import React, { useState, useEffect } from 'react';
import logo from './assets/logo.png';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import './App.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';


function Success() {
  return (
    <div className="success-page">
      <div className="success-card">
        <div className="success-icon">✓</div>
        <h1>Booking Confirmed!</h1>
        <p>Thank you for supporting our young coaches. Check your email for a calendar invitation with session details.</p>
        <div className="success-details">
          <h3>What's Next?</h3>
          <ul>
            <li>📧 Calendar invite sent to your email</li>
            <li>📍 Court location included in invite</li>
            <li>🎾 Bring comfortable clothes & water</li>
          </ul>
        </div>
        <a href="/" className="btn btn-primary">Book Another Session</a>
      </div>
    </div>
  );
}

function BookingPage() {
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSlots();
  }, []);

  const fetchSlots = async () => {
    try {
      const res = await fetch(`${API_URL}/api/availability`);
      const data = await res.json();
      setSlots(data.slots);
      setLoading(false);
    } catch (err) {
      setError('Failed to load available times');
      setLoading(false);
    }
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    if (!selectedSlot || !email || !name) return;

    setBooking(true);
    try {
      const res = await fetch(`${API_URL}/api/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slot: selectedSlot.start,
          email,
          name
        })
      });
      const data = await res.json();
      window.location.href = data.url;
    } catch (err) {
      setError('Failed to start checkout');
      setBooking(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const groupedSlots = slots.reduce((acc, slot) => {
    const date = formatDate(slot.start);
    if (!acc[date]) acc[date] = [];
    acc[date].push(slot);
    return acc;
  }, {});

  return (
    <div className="app">
      {/* Hero Section */}
      <header className="hero">
        <div className="hero-content">
          <img src={logo} alt="PickleBook Reservations" className="logo" />
          <h1>Book a Lesson, Change a Life</h1>
          <p className="hero-subtitle">
            Professional pickleball coaching from certified young coaches. 
            Every booking supports underprivileged youth in Hong Kong.
          </p>
        </div>
      </header>

      {/* How It Works */}
      <section className="how-it-works">
        <h2>How It Works</h2>
        <div className="steps">
          <div className="step">
            <div className="step-icon">📅</div>
            <h3>1. Pick a Time</h3>
            <p>Choose from available slots that fit your schedule</p>
          </div>
          <div className="step">
            <div className="step-icon">💳</div>
            <h3>2. Book & Pay</h3>
            <p>Secure payment through Stripe. Instant confirmation.</p>
          </div>
          <div className="step">
            <div className="step-icon">🎾</div>
            <h3>3. Play & Learn</h3>
            <p>Meet your coach at the court and enjoy your session!</p>
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="impact">
        <h2>Your Impact</h2>
        <div className="impact-content">
          <div className="impact-card">
            <div className="impact-icon">🌟</div>
            <h3>Certified Young Coaches</h3>
            <p>Our coaches are underprivileged youth who have worked hard to earn their coaching qualifications through HK Pickleball Foundation.</p>
          </div>
          <div className="impact-card">
            <div className="impact-icon">🤝</div>
            <h3>Community Partnership</h3>
            <p>Courts donated by Bay Pickle. Coaching by HKPF. Technology by Faith in Love Foundation. Together, we create opportunity.</p>
          </div>
          <div className="impact-card">
            <div className="impact-icon">💪</div>
            <h3>100% For Good</h3>
            <p>Proceeds go directly to supporting our young coaches and expanding the program to help more youth.</p>
          </div>
        </div>
      </section>

      {/* Booking Section */}
      <section className="booking-section" id="book">
        <h2>Book Your Session</h2>
        <p className="booking-intro">1-hour session including warm-up and cool-down • All skill levels welcome</p>
        
        {error && <p className="error">{error}</p>}
        
        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
            <p>Loading available times...</p>
          </div>
        ) : (
          <div className="booking-grid">
            <div className="slots-section">
              <h3>Select a Time</h3>
              {Object.keys(groupedSlots).length === 0 ? (
                <p className="no-slots">No available slots at the moment. Please check back later.</p>
              ) : (
                Object.entries(groupedSlots).map(([date, daySlots]) => (
                  <div key={date} className="day-group">
                    <h4>{date}</h4>
                    <div className="time-slots">
                      {daySlots.map((slot) => (
                        <button
                          key={slot.start}
                          className={`slot ${selectedSlot?.start === slot.start ? 'selected' : ''}`}
                          onClick={() => setSelectedSlot(slot)}
                        >
                          {formatTime(slot.start)}
                        </button>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="form-section">
              <h3>Your Details</h3>
              <form onSubmit={handleBooking}>
                <div className="form-group">
                  <label htmlFor="name">Name</label>
                  <input
                    id="name"
                    type="text"
                    placeholder="Your full name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                
                {selectedSlot && (
                  <div className="selected-summary">
                    <p><strong>Selected:</strong></p>
                    <p>{formatDate(selectedSlot.start)}</p>
                    <p>{formatTime(selectedSlot.start)}</p>
                  </div>
                )}
                
                <button
                  type="submit"
                  className="btn btn-primary btn-book"
                  disabled={!selectedSlot || !email || !name || booking}
                >
                  {booking ? 'Processing...' : 'Continue to Payment'}
                </button>
              </form>
            </div>
          </div>
        )}
      </section>

      {/* Partners Section */}
      <section className="partners">
        <h2>Our Partners</h2>
        <div className="partner-logos">
          <div className="partner">
            <div className="partner-placeholder">Faith in Love Foundation</div>
          </div>
          <div className="partner">
            <div className="partner-placeholder">HK Pickleball Foundation</div>
          </div>
          <div className="partner">
            <div className="partner-placeholder">Bay Pickle</div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <img src={logo} alt="PickleBook" className="footer-logo" />
          <p>A community initiative by Faith in Love Foundation & HK Pickleball Foundation</p>
          <div className="footer-links">
            <a href="mailto:info@faithinlove.org">Contact Us</a>
          </div>
          <p className="copyright">© 2025 PickleBook Reservations. All proceeds support youth development.</p>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<BookingPage />} />
        <Route path="/success" element={<Success />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;